"use client";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { signIn, signOut } from "next-auth/react";
import { MdOutlineVisibilityOff, MdOutlineVisibility } from "react-icons/md";
import { useSession } from "next-auth/react";
import { PrimaryBtn } from "@/components/elements/Buttons";
import React from "react";
import { User, UserSession } from "@/utils/types";
import Logo from "@/public/logo.png";
import ecommerceIllustration from "@/public/ecommerce-illustration.png";
import Image from "next/image";
import { SecondaryBtn } from "@/components/elements/Buttons";
import { useDialog } from "@/utils/DialogProvider";
import { POST_REQUEST } from "@/utils/fetch";
import CryptoJS from "crypto-js";
import { useTranslation } from "react-i18next";
import { isValidEmail } from "@/utils/fncts";
import {  toast } from "react-toastify";
import { Button } from "@mui/material";
import api from "@/app/lib/api";


export default function Login() {
  const router = useRouter();
  const session = useSession().data as UserSession;
  const { openDialog } = useDialog();
  const { t } = useTranslation();
 
  const [formData, setFormData] = useState({ email: "", password: "" });

  const [errorMessage, setErrorMessage] = useState("" );
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);


  const handleLogin = async () => {
    if (!isValidEmail(formData.email)) {
      setErrorMessage(t("incorrectEmail"));
      return { error: true };
    }

    try {
      const response = await signIn("credentials", {
        email: formData.email.replace(/\s/g, ""),
        password: formData.password,
        redirect: false,
      });

      if (response!.status == 200) 
        return { success: true };
      
      setErrorMessage(response?.error ?? t("incorrectEmailOrPassword"));
      toast.error(response?.error ?? t("incorrectEmailOrPassword"), { position: "top-center" });
      return { error: true };

    } catch (error : any) {
      console.error(error);
      return { error: true };
    }
  };

  useEffect(() => {
    if (session?.role == "admin" || session?.role === "super-admin") {
      router.push("/admin");
      return;
    }
    if (session?.role == "user") {
      router.push("/");
      return;
    }
    window.postMessage({ type: "save_user_id", id: session?.id }, "*");
  }, [session]);

  return (
    <>
      <form onSubmit={handleLogin} className="  c2 mc1 gap50 p50 vcenter mp0">

        <div className="grid gap30 h100vh  web-only">
          <div className="space-between">
            <Link href={"/"}>
              <Image src={Logo} alt="logo" className="h80px pointer" width={80} height={80} />
              <p className="f13 tcenter"> Amazigh  Import{" "} </p>
            </Link>
            <div></div>
          </div>
          <h1>{t("discoverInfinitePossibilities")}</h1>
          <Image src={ecommerceIllustration} alt="" className="h250px w100" />
        </div>


        <div className="auth-form login h100vh">
          <div></div>
          <div className="grid gap20">
            <div>
              <h2 className={"fw800"}> {t("login")} </h2>
              <p className={" secondary-color"}>{t("loginToContinue")}</p>
            </div>
            <div>
              <label htmlFor="">{t("emailLabel")} </label>
              <input
                className={"grey-bg"}
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value }) }
              />
              { errorMessage == t("incorrectEmail") && ( <p className="red f13">{errorMessage}</p> )}
            </div>

            <div>
              <label htmlFor="" > {t("password")}{" "} </label>
              <div className="relative">
                <input
                  className={"grey-bg"}
                  type={isPasswordVisible ? "text" : "password"}
                  id="password"
                  name="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value }) }
                />

                <span className="password-toggle" onClick={() => setIsPasswordVisible(!isPasswordVisible)} >
                  {isPasswordVisible ? ( <MdOutlineVisibilityOff /> ) : ( <MdOutlineVisibility /> )}
                </span>
              </div>
              { errorMessage != t("incorrectEmail") && ( <p className="red f13">{errorMessage}</p> )}
              <p
                className=" f13 dark-grey pointer"
                onClick={() => { openDialog(t("forgotPassword"), <ResetPasswordDialog />); }}
              >
                {t("forgotPassword")}
              </p>
            </div>

            <PrimaryBtn text={t("login")} submit={handleLogin} />
            <Link href="/signup" className="tcenter">
              <h3 className="dark-grey">{t("createAccount")} </h3>
            </Link>
            
            {<Link href="/" className="tcenter">
              <p className="dark-grey">{t("continueAsGuest")} </p>
            </Link>}

          </div>
          <div></div>
        </div>
      </form>
    </>
  );
}

export const ResetPasswordDialog = () => {
  const { t } = useTranslation();
  const { openDialog, closeDialog } = useDialog();
  
  const [email, setEmail] = useState("");
  const [error, setError] = useState(false);

  const handleSubmit = async () => {
    try {
      if (email == "") {
        setError(true);
        return {};
      }
      const response = await POST_REQUEST(`reset_pswrd`, { email: email });
      if ( (response.data as { message: string })?.message == "incorrect email" ) {
        setError(true);
        return;
      }
      
      if (response.success === true) {
        closeDialog();
        openDialog( t("confirmationCode"), <ConfimationCodeDialog email={email} user={response.data as User} /> );
        return { success: true };
      }

      return {};
    } catch (error) {
      return { error: true };
    }
  };

  return (
    <>
      <div className=" dialog-body">
        <div className="grid gap5">
          <label htmlFor="email" > {t("email")} </label>
          <input
            type="text"
            placeholder="ex : example@example.com"
            style={{ backgroundColor: "transparent", border: "1px solid #0f3683", }}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(false);
            }}
          />
          {error && email == "" && ( <p className="red f13">{t("requiredField")}</p> )}
          {error && email != "" && ( <p className="red f13"> {t("incorrectEmail")}</p> )}
        </div>
        <br />
      </div>
      <div className="dialog-footer">
        <div className="c1-auto">
          <div></div>
          <PrimaryBtn
            text={t("sendMail")}
            submit={() => {
              handleSubmit();
            }}
          />
        </div>
      </div>
    </>
  );
};

const ConfimationCodeDialog = ({  user,  email,}: {  user: User;  email: string;}) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  const [newUser, setUser] = useState(user);
  const { openDialog, closeDialog } = useDialog();
  const { t, i18n } = useTranslation();


  const handleSubmit = async () => {
    try {
      if (code == "" || newUser.reset_pswrd_code != code) {
        setError(true);
        return {};
      }
      if (newUser.reset_pswrd_code == code) {
        closeDialog();
        openDialog(t("edit"), <ChangePawssordDialog id={user.id} />);
        return { success: true };
      }
      return {};
    } catch (error) {
      return { error: true };
    }
  };
  return (
    <>
      <div className=" dialog-body">
        <p className="light-blue">{t("receiveConfirmationCodeMessage")}</p>
        <div className="grid gap5">
          <input
            type="text"
            placeholder={t("enterCode")}
            style={{ backgroundColor: "transparent", border: "1px solid #0f3683", }}
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setError(false);
            }}
          />
          {error && <p className="f13 red">{t("incorrectCode")} </p>}
        </div>
        <br />
      </div>
      <br />

      <div className="dialog-footer">
        <div className="c1-auto">
          <div></div>
          <div className="flex gap10">
            <SecondaryBtn
              text={"Renvoyer"}
              submit={async () => {
                const response = await POST_REQUEST(`reset_pswrd`, { email: email, });
                setUser(response.data as User);
              }}
            />
            <PrimaryBtn text={t("confirm")} submit={handleSubmit} />
          </div>
        </div>
      </div>
    </>
  );
};

const ChangePawssordDialog = ({ id }: { id: number }) => {
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language as "ar" | "fr" | "en";

  const {  closeDialog } = useDialog();
  
  const [passwords, setPasswords] = useState({ password: "", confirmPassword: "", });
  const [error, setError] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState({ password: false, confirmPassword: false, });

  const handleSubmit = async () => {
    try {
      if (passwords.password == "" || passwords.confirmPassword == "") {
        setError(true);
        return {};
      }
      
      if (passwords.password != passwords.confirmPassword) {
        setError(true);
        return {};
      }

      await api.users.update(id, { password: CryptoJS.SHA256(passwords.password).toString()});
    
      toast.success(t("passwordResetSuccessfully"), { position: "top-center", containerId: "", });
      setTimeout(closeDialog, 500);
      return { success: true };
      
    } catch (error  : any) {
      toast.error(`${t("error")} : ${error?.message}`, { position: "top-center" });
      return { error: true };
    }
  };
  return (
    <>
      <div className=" dialog-body">
        <div>
          <label > {t("password")}{" "} </label>
          <div className="relative">
            <input
              className={"br5"}
              type={isPasswordVisible.password ? "text" : "password"}
              id="password"
              name="password"
              required
              value={passwords.password}
              onChange={(e) => {
                setPasswords({ ...passwords, password: e.target.value });
                setError(false);
              }}
            />

            <span
              className="password-toggle"
              style={{ right: currentLocale == "ar" ? "" : "10px", left: currentLocale == "ar" ? "10px" : "", }}
              onClick={() => setIsPasswordVisible({...isPasswordVisible,password: !isPasswordVisible.password,})}
            >
              {isPasswordVisible ? ( <MdOutlineVisibilityOff /> ) : ( <MdOutlineVisibility /> )}
            </span>
          </div>
          {error && passwords.password == "" && ( <p className="red f13">{t("requiredField")} </p> )}
        </div>

        <div>
          <label > {t("confirmPasswordLabel")}{" "} </label>
          <div className="relative">
            <input
              className={"br5"}
              type={isPasswordVisible.confirmPassword ? "text" : "password"}
              id="password"
              name="password"
              required
              value={passwords.confirmPassword}
              onChange={(e) => {
                setPasswords({ ...passwords, confirmPassword: e.target.value });
                setError(false);
              }}
            />

            <span
              className="password-toggle"
              style={{ right: currentLocale == "ar" ? "" : "10px", left: currentLocale == "ar" ? "10px" : "", }}
              onClick={() =>
                setIsPasswordVisible({
                  ...isPasswordVisible,
                  confirmPassword: !isPasswordVisible.confirmPassword,
                })
              }
            >
              {isPasswordVisible ? ( <MdOutlineVisibilityOff /> ) : ( <MdOutlineVisibility /> )}
            </span>
          </div>
          {error && passwords.confirmPassword == "" && ( <p className="red f13">{t("fillRequiredFields")} </p> )}
          {error && passwords.confirmPassword != "" && ( <p className="red f13">{t("incorrectPassword")} </p> )}
        </div>
      </div>
      <br />
      <br />

      <div className="dialog-footer">
        <div className="c1-auto">
          <div></div>
          <PrimaryBtn
            text={t("confirm")}
            submit={() => {
              handleSubmit();
            }}
          />
        </div>
      </div>
    </>
  );
};
